import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useWhatsAppSettings,
  generateWhatsAppLink,
  generateGeneralWhatsAppMessage,
  generateProductWhatsAppMessage,
  generateOrderWhatsAppMessage,
} from "@/hooks/useWhatsApp";

interface WhatsAppButtonProps {
  variant?: "floating" | "inline" | "compact";
  productName?: string;
  orderNumber?: string;
  className?: string;
}

export const WhatsAppButton = ({
  variant = "inline",
  productName,
  orderNumber,
  className = "",
}: WhatsAppButtonProps) => {
  const { data: settings } = useWhatsAppSettings();

  if (!settings) return null;

  const getMessage = () => {
    if (orderNumber) {
      return generateOrderWhatsAppMessage(orderNumber);
    }
    if (productName) {
      return generateProductWhatsAppMessage(productName);
    }
    return generateGeneralWhatsAppMessage();
  };

  const whatsappUrl = generateWhatsAppLink(settings.phone_number, getMessage());

  if (variant === "floating") {
    return (
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg transition-all hover:scale-110 ${className}`}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
      </a>
    );
  }

  if (variant === "compact") {
    return (
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1.5 text-sm text-[#25D366] hover:text-[#128C7E] font-medium ${className}`}
      >
        <MessageCircle className="h-4 w-4" />
        <span>WhatsApp</span>
      </a>
    );
  }

  return (
    <Button
      asChild
      variant="outline"
      className={`bg-[#25D366] hover:bg-[#128C7E] text-white border-0 ${className}`}
    >
      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
        <MessageCircle className="h-4 w-4 mr-2" />
        Chat on WhatsApp
      </a>
    </Button>
  );
};
